import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';

import { FieldCheckbox } from './fields';
import { Error } from '../design/elements';
import FormField from './formField';
import { GroupWrapper, GroupTitle } from './css';
import Note from '../note';
import {
  BACKEND_KEYS,
  FIELDS_NAMES,
  administratorContact,
  mobileNumber,
  emailAddress,
  signingAuthority,
  phoneNumber,
  customerEmailAddress,
  signingAuthorityFM,
  fieldProvinceVUL
} from '../utils/constant';
import { fibreSpeed } from './vulcanPricing/fibeInternet/fibreInternetFieldNames';

const addressSections = [
  // BACKEND_KEYS.billingAddress,
  BACKEND_KEYS.serviceOrShippingAddress
];
const { city, postalCode, province, streetAddress } = FIELDS_NAMES;

const UserDetailsForm = ({
  data,
  formErrors,
  formFields,
  orderData,
  setFormFields,
  isEditing,
  isFormTouched,
  setIsFormTouched,
  disabled,
  isSameAsCheckboxChecked
}) => {
  const {
    name,
    note,
    formStepFields,
    sameAsCheckBox,
    dependsOnBackendField,
    dependsOnFieldName,
    dependsOnOrderDataKeys,
    backendKey,
    isItalicNote,
    isDefaultPopulated: isDefaultPopulatedField
  } = data;
  const [isSameAs, setIsSameAs] = useState(isSameAsCheckboxChecked);
  const [isDefaultPopulated, setIsDefaultPopulated] = useState(
    isDefaultPopulatedField
  );
  const handleFormField = (fieldName, value, type) => {
    const fields = { ...formFields };

    if (type === 'AddressDetails' && typeof value === 'object') {
      Object.keys(value).forEach(key => {
        fields[`${key}_${backendKey}`] = {
          ...fields[fieldName],
          value: value[key]
        };
      });
    } else {
      fields[fieldName] = { ...fields[fieldName], value };
    }
    if (!isFormTouched) {
      setIsFormTouched(true);
    }
    //only for vulcan webform service address
    if (
      fields[fieldProvinceVUL] &&
      formFields[fieldProvinceVUL].value !== fields[fieldProvinceVUL].value
    ) {
      fields[fibreSpeed].value = undefined;
    }

    setFormFields(fields);
  };

  const handleCustomerInformation = (fieldName, value) => {
    const fields = { ...formFields };
    const citykey = `${city}_${backendKey}`;
    const proviceKey = `${province}_${backendKey}`;
    const postalKey = `${postalCode}_${backendKey}`;
    if (fieldName === `${streetAddress}_${backendKey}`) {
      const addressParts = value && value.split(',');
      const partsLength = addressParts.length;

      if (partsLength > 3) {
        const cityValue = addressParts[partsLength - 3].trim();
        const provincePostalString = addressParts[partsLength - 2];
        if (
          provincePostalString &&
          provincePostalString.split(' ').length > 3
        ) {
          const [
            ,
            provinceValue = '',
            postalCodeValue = ''
          ] = provincePostalString.match(/(\D{2}) (\D\d\D \d\D\d)/);
          fields[citykey] = {
            ...fields[citykey],
            value: cityValue
          };
          fields[proviceKey] = {
            ...fields[proviceKey],
            value: provinceValue
          };
          fields[postalKey] = {
            ...fields[postalKey],
            value: postalCodeValue
          };
        } else {
          fields[citykey] = {
            ...fields[citykey],
            value: ''
          };
          fields[proviceKey] = {
            ...fields[proviceKey],
            value: ''
          };
          fields[postalKey] = {
            ...fields[postalKey],
            value: ''
          };
        }
      }
    }
    fields[fieldName] = {
      ...fields[fieldName],
      value
    };
    if (!isFormTouched) {
      setIsFormTouched(true);
    }
    setFormFields(fields);
  };

  const checkBoxName =
    sameAsCheckBox && sameAsCheckBox.fields && sameAsCheckBox.fields.name;

  const populateFormFields = () => {
    const fields = { ...formFields };
    formStepFields.forEach(({ fields: { name: fieldName, value } }) => {
      let fieldNewValue = value;
      const fieldPrefix = fieldName.split('_')[0];
      const fieldPostfix = fieldName.split('_')[1];
      if (isSameAs || isDefaultPopulated) {
        if (dependsOnBackendField) {
          const dependFormField =
            formFields[`${fieldPrefix}_${dependsOnBackendField}`];
          const dependFormFieldValue = dependFormField && dependFormField.value;
          if (
            (fieldPrefix === mobileNumber || fieldPrefix === emailAddress) &&
            fieldPostfix === signingAuthority
          ) {
            if (fieldPrefix === mobileNumber) {
              fieldNewValue = orderData[phoneNumber] || dependFormFieldValue;
            }
            if (fieldPrefix === emailAddress) {
              fieldNewValue =
                orderData[customerEmailAddress] || dependFormFieldValue;
            }
          } else {
            fieldNewValue =
              dependFormFieldValue ||
              (dependsOnBackendField === administratorContact
                ? orderData[`${fieldPrefix}`]
                : orderData[`${fieldPrefix}_${dependsOnBackendField}`]) ||
              (dependsOnBackendField === signingAuthorityFM
                ? orderData[`${fieldPrefix}`]
                : orderData[`${fieldPrefix}_${dependsOnBackendField}`]);
          }
        } else if (dependsOnFieldName) {
          const itemName = `${fieldPrefix}_${dependsOnFieldName}`;
          fieldNewValue = formFields[itemName] && formFields[itemName].value;
        } else if (dependsOnOrderDataKeys) {
          fieldNewValue = orderData[dependsOnOrderDataKeys[fieldName]];
        }
      } else {
        fieldNewValue = '';
      }
      fields[fieldName] = { ...fields[fieldName], value: fieldNewValue };
    });

    if (checkBoxName) {
      fields[checkBoxName] = {
        isVisible: true,
        checked: isSameAs,
        value: isSameAs
      };
    }

    setFormFields(fields);
  };

  useEffect(() => {
    populateFormFields();
  }, [isSameAs]);

  useEffect(() => {
    if (isDefaultPopulated && orderData && orderData.id) {
      populateFormFields();
      setIsDefaultPopulated(false);
      setIsSameAs(true);
    }
  }, [isDefaultPopulated, orderData]);

  const onIsSameAsChange = ({ target: { checked } }) => {
    setIsSameAs(checked);
    if (!isFormTouched) setIsFormTouched(true);
  };

  useEffect(() => {
    if (isEditing && orderData && orderData.id) {
      const fields = { ...formFields };
      formStepFields.forEach(({ fields: { name: fieldName } }) => {
        fields[fieldName] = {
          ...fields[fieldName],
          value: orderData[fieldName]
        };
      });
      setFormFields(fields);
      if (isFormTouched) setIsFormTouched(false);
      if (orderData[checkBoxName]) setIsSameAs(true);
    }
  }, [isEditing, orderData]);
  // const tip =
  // note &&
  // note.content[0] &&
  // note.content[0].content[0] &&
  // note.content[0].content[0].value;
  return (
    <GroupWrapper>
      <GroupTitle>{name}</GroupTitle>
      {note && note.content && (
        <Note
          type="richText"
          data={note}
          isShowNoteBackground={true}
          isItalicNote={isItalicNote}
        />
      )}
      {sameAsCheckBox && (
        <FieldCheckbox
          data={{
            readonly: disabled,
            ...sameAsCheckBox.fields,
            onChange: onIsSameAsChange,
            checked: isSameAs
          }}
        />
      )}
      {formStepFields.map(({ fields, sys: { id: sysId } }, index) => {
        const key = `UserDetails${name}${index}`;
        const error = formErrors[fields.name];

        const fieldData = {
          ...fields,
          value: formFields[fields.name] && formFields[fields.name].value,
          disabled,
          isSameAs,
          sysId,
          onChange: ({ target: { name: fieldName, value } }) => {
            if (addressSections.includes(backendKey)) {
              handleCustomerInformation(fieldName, value);
            } else {
              handleFormField(fieldName, value, fields.type);
            }
          }
        };
        return (
          <Fragment key={key}>
            <FormField data={fieldData} />
            {error && error.text && <Error text={error.text} />}
          </Fragment>
        );
      })}
    </GroupWrapper>
  );
};

UserDetailsForm.propTypes = {
  data: PropTypes.shape({
    dependsOnFieldName: PropTypes.string,
    dependsOnOrderDataKeys: PropTypes.objectOf(PropTypes.object).isRequired,
    backendKey: PropTypes.string,
    dependsOnBackendField: PropTypes.string,
    formStepFields: PropTypes.arrayOf(PropTypes.object),
    name: PropTypes.string.isRequired,
    sameAsCheckBox: PropTypes.objectOf(PropTypes.object).isRequired,
    isDefaultPopulated: PropTypes.bool
  }).isRequired,
  formErrors: PropTypes.objectOf(PropTypes.object),
  formFields: PropTypes.objectOf(PropTypes.object).isRequired,
  orderData: PropTypes.objectOf(PropTypes.object).isRequired,
  setFormFields: PropTypes.func.isRequired,
  isEditing: PropTypes.string.isRequired,
  isFormTouched: PropTypes.bool.isRequired,
  setIsFormTouched: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isSameAsCheckboxChecked: PropTypes.bool
};

UserDetailsForm.defaultProps = {
  formErrors: {},
  disabled: false,
  isSameAsCheckboxChecked: false
};

export default UserDetailsForm;
